import type {
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  useQuery,
} from '@tanstack/react-query'

import * as PlaceKeys from '../reactQueryKeys/place'

import { getPlaceByLatLong, getTypeaheadSuggestions } from 'api/search'
import { getSearchItemDestinationId } from 'lib/search/searchItem'
import { isAnywhereDestinationSearch } from 'lib/search/searchUtils'
import type { GeoPlace } from 'tripPlanner/types/common'
import * as SearchKeys from '../reactQueryKeys/search'

// HOTEL_SEARCH_TYPEAHEAD_TYPES without stations, airports or hotels: a stop names an area.
const STOP_PLACE_TYPEAHEAD_TYPES: Array<App.SearchPlaceType> = [
  'province_state',
  'neighborhood',
  'city',
  'point_of_interest',
  'high_level_region',
  'country',
  'multi_city_vicinity',
  'continent',
  'colloquial_area',
]

function getFirstPlaceId(suggestions: Array<App.SearchItem>): string | undefined {
  return suggestions
    .map(getSearchItemDestinationId)
    .find((placeId) => !!placeId && !isAnywhereDestinationSearch(placeId))
}

export const useSearchPlace = (
  { lat, lng, type }: Partial<GeoPlace['point']> & { type: App.PlaceType },
  options?: UseQueryOptions<
    App.Place | undefined,
    unknown,
    App.Place | undefined
  >,
): UseQueryResult<App.Place | undefined, unknown> => {
  return useQuery({
    queryKey:
      lat !== undefined && lng !== undefined ?
          PlaceKeys.searchPlaceDetail(lat, lng, type) :
        undefined,
    queryFn:
      lat !== undefined && lng !== undefined ?
          () => getPlaceByLatLong(lat, lng, type) :
          () => Promise.resolve(undefined),
    ...options,
  })
}

export const useLEPlaceByCoordinates = (
  lat: number,
  lng: number,
): UseQueryResult<App.Place, unknown> => {
  return useQuery(
    SearchKeys.placeByCoordinates(lat, lng),
    () => getPlaceByLatLong(lat, lng),
    {
      staleTime: Infinity,
    },
  )
}

/** The first LE place typeahead finds for a bare place name. */
export const usePlaceIdByName = (
  { name, region }: { name?: string; region: string },
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<string | undefined, unknown> => {
  return useQuery({
    queryKey: SearchKeys.placeByName(name ?? '', region),
    queryFn: () => getTypeaheadSuggestions(name ?? '', region, {
      types: STOP_PLACE_TYPEAHEAD_TYPES,
      vertical: 'hotel',
      priority: 'next',
      limit: 10,
    }),
    select: getFirstPlaceId,
    enabled: enabled && !!name,
    staleTime: Infinity,
  })
}
